class Carousel3D extends HTMLElement {
  constructor() {
    super();

    this.els = this._getEls();
    this._isDragging = false;
    this._currentPositionDegrees = 0;
    this._slideNextListener = this._slideNext.bind(this);
    this._slidePrevListener = this._slidePrev.bind(this);
    this.init();
  }

  /**
   * Initialises the Carousel and adds event listeners
   */
  init() {
    this._setProperties();

    this.resizeObserver = new ResizeObserver(() => this._setProperties());
    this.resizeObserver.observe(this);

    this.els.nextButton.addEventListener('click', this._slideNextListener);
    this.els.prevButton.addEventListener('click', this._slidePrevListener);
    ["touchstart","mousedown"].forEach(eventType => this.addEventListener(eventType, (e) => this._handleDragStart(e)));
    ["touchend", "mouseup"].forEach(eventType => this.addEventListener(eventType, (e) => this._handleDragEnd(e)));
    ["touchmove", "mousemove"].forEach(eventType => this.addEventListener(eventType, (e) => this._handleDrag(e)));

    // Shows the gallery once the images have been correctly positioned
    setTimeout(() => {
      this.classList.remove('slider-3dcarousel--fade-in');
    }, 10);
  }

  /**
   * Sets the current position of the Carousel
   */
  set currentPositionDegrees (value) {
    this._currentPositionDegrees = value;
    this._updateAllSlides()
  }

  /**
   * Sets the internal and CSS properties
   */
  _setProperties() {
    const SLIDE_SIZE_MULTIPLIER = 1.1; // Changes the relative size of the slides
    const SLIDE_ASPECT_RATIO = 2/3;
    const { height, width } = this.getBoundingClientRect();

    this.containerWidth = width;
    this.containerHeight = height;
    this.slideHeight = Math.round(width * SLIDE_SIZE_MULTIPLIER);
    this.slideWidth = Math.round(width * SLIDE_SIZE_MULTIPLIER * SLIDE_ASPECT_RATIO);
    
    this.style.setProperty('--carousel-width', `${width}px`);
    this.style.setProperty('--image-wrapper-pos-x', `${parseInt(width) / 2}px`);
    this.style.setProperty('--image-wrapper-pos-y', `${parseInt(height) / 2}px`);
    this.style.setProperty('--slide-width', `${this.slideWidth}px`);
    this.style.setProperty('--slide-height', `${this.slideHeight}px`);

    this._updateAllSlides();
  }

  /**
   * Updates all slides CSS properties
   */
  _updateAllSlides(slideOffset = 0) {
    this.els.slides.forEach(slide => this._updateSlideProperties(slide, slideOffset));
  }

  /**
   * Updates an individual slide's CSS properties
   * @param {HTMLElement} slide - The slide Element
   * @param {number} slideOffset - Degree value to rotate slide from current position
   */
  _updateSlideProperties(slide, slideOffset = 0) {
    const POSITION_MULTIPLIER = 0.65; // Changes the spread of the slides
    const MAX_DEPTH = 1000;
    const maxTranslate = this.containerWidth * POSITION_MULTIPLIER;
    const positionDegrees = parseInt(slide.dataset.positionDegrees) + this._currentPositionDegrees + slideOffset;
    const posX = Math.round(Math.round(Math.sin(this._degreesToRadians(positionDegrees)) * maxTranslate * -1) - (this.slideWidth / 2));
    const posZ = Math.round(Math.cos(this._degreesToRadians(positionDegrees)) * (MAX_DEPTH / 2)) - MAX_DEPTH / 2;
    const isBackgroundImage = posZ < MAX_DEPTH / -2;

    slide.style.setProperty('--slide-pos-x', `${posX}px`);
    slide.style.setProperty('--slide-pos-z', `${posZ}px`);
    slide.style.setProperty('--slide-opacity', `${isBackgroundImage ? 0.5 : 1}`);
  }

  /**
   * Moves to the next slide
   */
  _slideNext() {
    this.currentPositionDegrees = this._currentPositionDegrees + 60;
    this._updateAllSlides()
  }

  /**
   * Moves to the previous slide
   */
  _slidePrev() {
    this.currentPositionDegrees = this._currentPositionDegrees - 60;
    this._updateAllSlides()
  }


  /**
   * Handles Drag Start event
   * @param {Event} event - The mouse/pointer event 
   */
  _handleDragStart(event) {
    this._isDragging = true;
    this._startingPosX = event.type.toLowerCase() === 'touchstart' ? event.touches[0].clientX : event.clientX;
  }

  /**
   * Handles Drag End event
   * @param {Event} event - The mouse/pointer event 
   */
  _handleDragEnd(event) {
    event.stopPropagation();
    this._isDragging = false;
    this._updateAllSlides();
  }

  /**
   * Handles Dragging event
   * @param {Event} event - The mouse/pointer event 
   */
  _handleDrag(event) {
    if (!this._isDragging) return;
    const posX = event.type.toLowerCase() === 'touchmove' ? event.touches[0].clientX : event.clientX;
    const draggedDelta = this._startingPosX - posX;
    const dragThreshold = this.containerWidth / 5;
    const slideOffset = 40 * (draggedDelta / dragThreshold);
    this._updateAllSlides(slideOffset);

    if (Math.abs(draggedDelta) > dragThreshold) {
      event.preventDefault();
      
      this._isDragging = false;
      if (draggedDelta > 0) {
        this._slideNext();
      } else {
        this._slidePrev();
      }
    }
  }

  /**
   * Gets all elements from the DOM
   */
  _getEls() {
    return {
      innerContainer: this.querySelector('[data-inner-container]'),
      imageWrapper: this.querySelector('[data-image-wrapper]'),
      slides: [...this.querySelectorAll('[data-slide]')],
      nextButton: this.querySelector('[data-next-button]'),
      prevButton: this.querySelector('[data-prev-button]'),
    };
  }

  /**
   * Converts degrees into radians
   * @param {number} degrees - The value in the degrees
   * 
   * @returns {number} The value in radians
   */
  _degreesToRadians(degrees) {
    return degrees * (Math.PI/180);
  }
}

if (!customElements.get('carousel-3d')) {
  customElements.define('carousel-3d', Carousel3D);
}